<template>
  <div class="wrapper mt-2">
    <validation-observer
      v-if="loaded"
      ref="notes-filters-form"
      tag="form"
      class="search-form search-form-notes mb-3">

      <f-button
        v-if="filtersBtnVisible"
        type="button"
        theme="dark"
        class="block full-width mb-3"
        @click.prevent="toggleFilters">
        <template v-if="filtersOpen">
          {{ $t('advisersFinder.collapseFilters')}}
        </template>
        <template v-else>
          {{ $t('advisersFinder.expandFilters')}}
        </template>
      </f-button>

      <transition name="slide">
        <div v-if="!filtersBtnVisible || filtersOpen">
          <div class="flex search-form-top flex-align-items-baseline">
            <div class="search-form-inputs flex full-width">
              <f-input-wrapper
                prependIcon="search"
                class="search search-field mb-1-ms">
                <f-input
                  :placeholder="$t('ui.search')"
                  v-model="filters.search"/>
              </f-input-wrapper>

              <v-date-picker
                v-model="createdAtRange"
                is-range
                mode="dateTime"
                is24hr
                :locale="$i18n.locale"
                class="date">
                <template v-slot="{ inputValue, togglePopover }">
                  <div class="flex justify-center items-center">
                    <input
                      :value="getDateRangeFormatted(inputValue)"
                      @click="togglePopover()"
                      :placeholder="$t('notesList.createDate')"
                      class="form-control ml-1 full-width ml-0-ms" />
                  </div>
                </template>
              </v-date-picker>
            </div>

            <div class="search-form-buttons flex">
              <f-button
                theme="primary"
                class="ml-1 mb-1-ms ml-0-sm"
                @click.prevent="validateFiltersForm">
                {{ $t('ui.search')}}
              </f-button>

              <f-button
                theme="warning"
                class="ml-1"
                @click.prevent="clearFilters">
                {{ $t('advisersFinder.resetFilters')}}
              </f-button>
            </div>
          </div>
        </div>
      </transition>
    </validation-observer>

    <div
      v-if="loaded && !loadError"
      :class="{'is-loading': !loaded}">
      <f-button
        theme="dark"
        class="ml-auto mb-3 block"
        @click.prevent="$bus.$emit('note-popup-show', customerID)">
        {{ $t('notesList.addNotes') }}
      </f-button>

      <f-list
        ref="all-notes-list"
        class="notes-list"
        :data-mobile-actions-count="1"
        :loaded="loaded"
        :listSettings="notesListSettings"
        :listItems="notesFormatted" />
    </div>

    <pagination
      :hasWrapper="true"
      :meta-data="paginationMetaData"
      :elementsCounterLabel="'notes'" />

    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <div class="wrapper mt-3">
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
    <note-popup />
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import NotePopup from '@/components/popups/NotePopup.vue';

export default {
  name: 'notes-list',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination,
    'note-popup': NotePopup
  },
  props: {
    itemsList: {
      type: Array
    },
    customerID: {
      type: Number,
      required: true
    }
  },
  computed: {
    notesListSettings () {
      let settingList =
      [
        {
          title: 'ID',
          variableName: ['id'],
          type: 'String'
        },
        {
          title: this.$t('notesList.createDate'),
          variableName: ['created_at'],
          type: 'String'
        },
        {
          title: this.$t('notesList.lastModifyDate'),
          variableName: ['updated_at'],
          type: 'String'
        },
        {
          title: '',
          mobileTitle: this.$t('notesList.contentOfNote'),
          variableName: ['note'],
          type: 'String',
          additionalClass: 'text-ellipsis'
        },
        {
          title: '',
          variableName: ['actionsList'],
          type: 'actions',
          items: [
            {
              type: 'button',
              disabled: false,
              event: 'note-edit',
              label: this.$t('ui.open'),
              name: 'edit'
            },
            {
              type: 'button',
              disabled: false,
              event: 'note-delete',
              label: this.$t('ui.remove'),
              theme: 'danger',
              name: 'delete'
            }
          ]
        }
      ]

      return settingList;
    },
    notesFormatted () {
      let notesList = this.notes;

      if (!notesList.length) {
        return [];
      }

      return notesList.map(item => {
        item.created_at = this.formatDate(item.created_at);
        item.updated_at = this.formatDate(item.updated_at);
        item.actionsList = ['edit', 'delete'];

        return item;
      });
    }
  },
  watch: {
    'createdAtRange.start': function (newValue) {
      if (newValue === null) {
        this.filters.created_at_from = null;
      }

      this.filters.created_at_from = this.formatDate(newValue);
    },
    'createdAtRange.end': function (newValue) {
      if (newValue === null) {
        this.filters.created_at_to = null;
      }

      this.filters.created_at_to = this.formatDate(newValue);
    }
  },
  data() {
    return {
      notes: [],
      loaded: false,
      loadError: false,
      allAdvisers: [],
      advisersLoaded: false,
      removeInProgress: false,
      filters: {
        search: '',
        created_at_from: '',
        created_at_to: ''
      },
      filtersOpen: false,
      filtersBtnVisible: false,
      createdAtRange: {
        sart: null,
        end: null
      }
    };
  },
  mounted () {
    this.$bus.$on('note-edit', this.editNote);
    this.$bus.$on('note-delete', this.removeNote);
    this.$bus.$on('notes-list-reload', this.loadFilteredData);

    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let endpointParams = {
        where: {
          adviser_customer_id: this.customerID
        }
      };

      if (this.filters.search) {
        endpointParams.where.search = this.filters.search;
      }

      if (this.filters.created_at_from) {
        endpointParams.where.created_at_from = this.filters.created_at_from;
      }

      if (this.filters.created_at_to) {
        endpointParams.where.created_at_to = this.filters.created_at_to;
      }

      let enpointUrl = 'api/adviser/customers/notes/items?page=' + this.currentPage;

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: enpointUrl,
        listField: 'notes',
        pagination: true,
        params: endpointParams
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        created_at_from: '',
        created_at_to: ''
      };

      this.createdAtRange = {};

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['notes-filters-form'], this.debouncedLoadFilteredData);
    },
    removeNote (note) {
      this.removeInProgress = true;
      let noteId = typeof note === 'object' ? note.id : note;

      ListUtils.removeItem(this, {
        endpointBase: '/api/adviser/customers/notes/delete/',
        id: noteId,
        successTitle: this.$t('notesList.deleteNote'),
        successText: this.$t('notesList.deleteSelectNote'),
        confirmTitle: this.$t('notesList.areYouSureDeleteSelectNote'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('notesList.deleteNoteError'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    formatDate (timestamp) {
      if (!timestamp) {
        return this.$t('ui.none').toLowerCase();
      }

      let dateObject = new Date(timestamp);

      return [
        dateObject.getFullYear(),
        dateObject.getMonth() + 1,
        dateObject.getDate()
      ].map(n => n < 10 ? '0' + n : n).join('-') + ' ' + [
        dateObject.getHours(),
        dateObject.getMinutes()
      ].map(n => n < 10 ? '0' + n : n).join(':');
    },
    getDateRangeFormatted (dateRange) {
      if (!dateRange || (!dateRange.start && !dateRange.end)) {
        return null;
      }

      let dateRangeStart = dateRange.start;
      let dateRangeEnd = dateRange.end;

      if (!dateRangeStart) {
        dateRangeStart = 'od';
      }
      if (!dateRangeEnd) {
        dateRangeEnd = 'do';
      }

      return dateRangeStart + ' - ' + dateRangeEnd;
    },
    editNote (note) {
      this.$bus.$emit('note-popup-show', this.customerID, note);
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    }
  },
  beforeDestroy () {
    this.$bus.$off('note-edit', this.editNote);
    this.$bus.$off('note-delete', this.removeNote);
    this.$bus.$off('notes-list-reload', this.loadFilteredData);
  }
};
</script>

